"use client";

import Image from 'next/image';
import React, { useCallback, useEffect, useState } from 'react';
import { autoComplete } from '@/actions/places/autoComplete';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocation } from '@fortawesome/pro-regular-svg-icons';
import useSetSearchParams from '@/hooks/useSetSearchParams';
import { useItinerarySteps } from '@/core/stores/itinerarySteps';
import { deslugify } from '@/utils/slugify';
import NextLink from '@/components/NextLink';
import { useOutsideClick } from '@/hooks/useOutsideClick';

export default function SearchInput() {


    const [cityText, setCity] = useState("");
    const [loading, setLoading] = useState<boolean>(false);
    const [fetchError, setFetchError] = useState("");
    const [predictions, setPredictions] = useState<
        { id: string; name: string, country: string, city: string }[]
    >([]);
    const [showCities, setShowCities] = useState(false);

    const { destination, setDestination } = useItinerarySteps();

    const entriesModal = useOutsideClick(() => setShowCities(false))

    const handleSetDestination = (
        value:
            | { name: string; id: string; country: string }
            | ((prev: { name: string; id: string; country: string }) => { name: string; id: string; country: string })
    ) => {
        if (typeof value === 'object' && value !== null && value.name) {
            // value is the destination object
            setDestination(value);

        }
    };

    const handleInputChange = useCallback((event: any) => {
        setCity(event.target.value)
        if (event.target.value) {
            setShowCities(true);
        } else {
            setShowCities(false);
        }
    }, [])


    const get = async (city: string) => {
        setLoading(true);
        try {

            const res = await autoComplete(city.split(', ')[1] || city);

            const { result } = await JSON.parse(res)

            const cityNames = result.data.cities?.map(
                (prediction: any, index: number) => {
                    return {
                        ...prediction,
                        id: index,
                    };
                },
            );
            setLoading(false);
            setPredictions(cityNames || []);
        } catch (err) {
            setLoading(false);
            setFetchError("Something went wrong!");
        }
    };

    useEffect(() => {
        setFetchError("");
        if (cityText) {
            setLoading(true);
        }

        const getData = setTimeout(() => {
            if (cityText && cityText.length > 0) {
                get(cityText);
            }
        }, 400);

        return () => clearTimeout(getData);
    }, [cityText]);

    return (
        <div className='mt-10 w-full flex flex-row-reverse lg:flex-row gap-2 lg:gap-12 lg:h-24 items-center justify-between max-w-5xl rounded-2xl lg:shadow-md border border-gray-200 p-2 lg:px-16 lg:py-4'>

            <div className="hidden lg:block lg:w-9/12 relative">
                <div className="flex items-center lg:border rounded-2xl h-[70px] lg:px-4 bg-transparent relative">
                    <svg className='hidden lg:block' width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_1116_163333)">
                            <path d="M12.7403 0H12.7188C7.23156 0 2.78125 4.44844 2.78125 9.9375C2.78125 16.073 9.19562 22.5567 12.3184 23.9156C12.4493 23.9724 12.5904 24.0017 12.733 24.0017C12.8757 24.0017 13.0168 23.9724 13.1477 23.9156C16.2695 22.5563 22.6741 16.0725 22.6741 9.9375C22.6759 4.44844 18.2275 0 12.7403 0Z" fill="#F96864" />
                            <path d="M2.78125 9.93751C2.78125 13.0341 4.41578 16.2188 6.4375 18.7899C13.6183 18.7655 19.4331 12.9375 19.4331 5.7497C19.4341 4.53891 19.2664 3.3339 18.9348 2.16939C17.1765 0.76205 14.9906 -0.00323244 12.7384 1.02629e-05H12.7169C7.22969 1.02629e-05 2.78125 4.44845 2.78125 9.93751Z" fill="#F96864" />
                            <path d="M12.7266 13.2656C12.0312 13.2656 11.3515 13.0594 10.7734 12.6731C10.1952 12.2868 9.74464 11.7378 9.47855 11.0954C9.21246 10.453 9.14284 9.7461 9.27849 9.06414C9.41414 8.38217 9.74897 7.75575 10.2406 7.26408C10.7323 6.77241 11.3587 6.43758 12.0407 6.30193C12.7227 6.16628 13.4295 6.2359 14.0719 6.50199C14.7143 6.76808 15.2634 7.21868 15.6497 7.79683C16.036 8.37497 16.2422 9.05468 16.2422 9.75C16.2422 10.2117 16.1513 10.6688 15.9746 11.0954C15.7979 11.5219 15.5389 11.9095 15.2125 12.2359C14.886 12.5624 14.4985 12.8213 14.0719 12.998C13.6454 13.1747 13.1882 13.2656 12.7266 13.2656Z" fill="white" />
                        </g>
                        <defs>
                            <clipPath id="clip0_1116_163333">
                                <rect width="24" height="24" fill="white" transform="translate(0.726562)" />
                            </clipPath>
                        </defs>
                    </svg>

                    <div className='hidden lg:block flex-1'>
                        <p className='pl-5 text-xs text-gray-500'>Where to</p>
                        <input
                            type="text"
                            value={cityText}
                            onChange={(e) => handleInputChange(e)}
                            placeholder="Enter Your Destination"
                            className="w-full font-normal mt-1 bg-transparent outline-none text-gray-800 pl-5 placeholder-gray-400"
                        />
                    </div>

                    <NextLink href={'/trip-maker/custom?step=1'} className="lg:ml-3 h-12 bg-main-500 flex items-center justify-center gap-4 text-white px-4 py-2 rounded-2xl hover:bg-main-700 transition">
                        <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="10.3112" cy="9.58341" r="7.91667" stroke="white" strokeWidth="1.5" />
                            <path d="M17.3945 16.6667L19.0612 18.3334" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                        Search
                    </NextLink>

                    {/* Popup dropdown */}
                    {showCities && (
                        <div ref={entriesModal} className="absolute top-[98%] left-0 mt-2 w-6/12 bg-white border rounded-xl shadow-lg z-10 p-4">

                            {loading ?
                                <div className="flex w-full flex-col gap-8 pt-1">
                                    <div className="flex w-full items-center gap-3">
                                        <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                        <div className="flex flex-col gap-2">
                                            <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                            <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                        </div>
                                    </div>
                                    <div className="flex w-full items-center gap-3">
                                        <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                        <div className="flex flex-col gap-2">
                                            <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                            <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                        </div>
                                    </div>
                                    <div className="flex w-full items-center gap-3">
                                        <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                        <div className="flex flex-col gap-2">
                                            <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                            <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                        </div>
                                    </div>
                                    <div className="flex w-full items-center gap-3">
                                        <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                        <div className="flex flex-col gap-2">
                                            <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                            <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                        </div>
                                    </div>
                                    <div className="flex w-full items-center gap-3">
                                        <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                        <div className="flex flex-col gap-2">
                                            <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                            <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                        </div>
                                    </div>
                                </div>
                                : !fetchError &&
                                <div>
                                    {predictions?.map((item) => (
                                        <div
                                            onTouchStart={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                setShowCities(false);
                                                handleSetDestination({
                                                    name: item.name.split(",")[0],
                                                    id: item.id,
                                                    country: item.country,
                                                });
                                                setCity(`${deslugify(item.country)} ${item.name ? `, ${deslugify(item.name)}` : ''}` as string);
                                            }}
                                            onClick={() => {
                                                setShowCities(false);
                                                handleSetDestination({
                                                    name: item.name.split(",")[0],
                                                    id: item.id,
                                                    country: item.country
                                                    ,
                                                });
                                                setCity(`${deslugify(item.country)} ${item.name ? `, ${deslugify(item.name)}` : ''}` as string);
                                            }}
                                            className="group grid cursor-pointer grid-cols-[30px,_1fr] place-items-start items-center gap-1 rounded-xl 
                                             px-1 py-2 transition-colors duration-200 hover:bg-gray-100 hover:text-emerald-700 lg:w-full"
                                            key={item.id}
                                        >
                                            <FontAwesomeIcon icon={faLocation} className="w-5 justify-self-center text-gray-600" />
                                            <div className="flex flex-col  gap-1">
                                                <p className="font-semibold capitalize text-base text-[#202020]">
                                                    {item.name.toLowerCase()}
                                                </p>
                                                <span className="text-xs capitalize font-normal text-gray-600">
                                                    {item.country}{" "}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>}

                            {fetchError && <div className="flex w-full flex-col items-center justify-center gap-4 ">
                                <p className="text-orange-500">{fetchError}</p>

                                <Image
                                    className="w-32"
                                    src={"https://assets.bamboo.travel/wrong.svg"}
                                    width={300}
                                    height={300}
                                    alt="Nothing Found"
                                />
                            </div>}
                        </div>
                    )}
                </div>
            </div>

            <div className="inline-block border lg:border-none border-secondary-500 lg:bg-gradient-to-r from-[#33B2FF] via-[#8B4FFE] to-[#EF5FD9] lg:p-[2px] rounded-2xl">
                <NextLink href={"/trip-maker/lucky-trip"} className='px-4 py-3 flex gap-3 justify-center items-center h-12 bg-secondary-50 rounded-2xl'>
                    <Image
                        alt=''
                        width={40}
                        height={40}
                        className='w-6'
                        src={`${process.env.NEXT_PUBLIC_STORAGE_URL}assets/e3319efc-18d0-4b03-9284-d05cd34b5a38-planingo.png`}
                    />
                    <p className='text-secondary-500 whitespace-nowrap'>Surprise Me</p>
                    <svg className='hidden lg:block' width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="8.72526" cy="7.99992" r="6.66667" stroke="#00C950" />
                        <path d="M8.72656 11.3333V7.33325" stroke="#00C950" strokeLinecap="round" />
                        <circle cx="0.666667" cy="0.666667" r="0.666667" transform="matrix(1 0 0 -1 8.05859 6)" fill="#00C950" />
                    </svg>
                </NextLink>
            </div>

            <div className="lg:hidden inline-block w-6/12 border border-gray-200 rounded-2xl">
                <NextLink href={"/trip-maker/custom"} className='px-4 py-3 flex gap-3 justify-center items-center h-12 bg-gray-50 rounded-2xl'>
                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_1116_163333)">
                            <path d="M12.7403 0H12.7188C7.23156 0 2.78125 4.44844 2.78125 9.9375C2.78125 16.073 9.19562 22.5567 12.3184 23.9156C12.4493 23.9724 12.5904 24.0017 12.733 24.0017C12.8757 24.0017 13.0168 23.9724 13.1477 23.9156C16.2695 22.5563 22.6741 16.0725 22.6741 9.9375C22.6759 4.44844 18.2275 0 12.7403 0Z" fill="#F96864" />
                            <path d="M2.78125 9.93751C2.78125 13.0341 4.41578 16.2188 6.4375 18.7899C13.6183 18.7655 19.4331 12.9375 19.4331 5.7497C19.4341 4.53891 19.2664 3.3339 18.9348 2.16939C17.1765 0.76205 14.9906 -0.00323244 12.7384 1.02629e-05H12.7169C7.22969 1.02629e-05 2.78125 4.44845 2.78125 9.93751Z" fill="#F96864" />
                            <path d="M12.7266 13.2656C12.0312 13.2656 11.3515 13.0594 10.7734 12.6731C10.1952 12.2868 9.74464 11.7378 9.47855 11.0954C9.21246 10.453 9.14284 9.7461 9.27849 9.06414C9.41414 8.38217 9.74897 7.75575 10.2406 7.26408C10.7323 6.77241 11.3587 6.43758 12.0407 6.30193C12.7227 6.16628 13.4295 6.2359 14.0719 6.50199C14.7143 6.76808 15.2634 7.21868 15.6497 7.79683C16.036 8.37497 16.2422 9.05468 16.2422 9.75C16.2422 10.2117 16.1513 10.6688 15.9746 11.0954C15.7979 11.5219 15.5389 11.9095 15.2125 12.2359C14.886 12.5624 14.4985 12.8213 14.0719 12.998C13.6454 13.1747 13.1882 13.2656 12.7266 13.2656Z" fill="white" />
                        </g>
                        <defs>
                            <clipPath id="clip0_1116_163333">
                                <rect width="24" height="24" fill="white" transform="translate(0.726562)" />
                            </clipPath>
                        </defs>
                    </svg>
                    <p className='text-gray-500 text-nowrap'>Destination</p>
                </NextLink>
            </div>

        </div>
    );
}


